import type { DocumentReference } from '@medplum/fhirtypes';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { type AoeGroup, OrderEntryQuestionsSection } from './OrderEntryQuestionsSection';
import { RequisitionDocumentsSection } from './RequisitionDocumentsSection';
import type { DetailSection } from './serviceRequestUtils';
import { SpecimenLabelSection } from './SpecimenLabelSection';

const ORDER_DETAILS_VALUE = 'order-details';

export type LabOrderOrderingProps = {
  /** Order metadata rows (order date, ordering provider, performing lab, etc.). */
  sections: DetailSection[];
  requisitionDocuments?: DocumentReference[];
  isLoadingRequisitionDocuments: boolean;
  showRequisition: boolean;
  specimenLabelDocuments?: DocumentReference[];
  isLoadingSpecimenLabelDocuments: boolean;
  showSpecimenLabel: boolean;
  aoeGroups?: AoeGroup[];
  isLoadingAoeGroups: boolean;
  showAoe: boolean;
  /**
   * Whether the "Order details" accordion starts expanded. It's always collapsible; open it by
   * default for orders without results (where the details are the main content) and leave it
   * collapsed once results are in and become the focus.
   */
  defaultOpen: boolean;
};

/**
 * Renders the lab order's ordering details — the order metadata, order-entry questions, and the
 * requisition and specimen-label documents — grouped into one collapsible "Order details" accordion
 * within the {@link LabOrderDetail} view.
 */
export const LabOrderOrdering = ({
  sections,
  requisitionDocuments,
  isLoadingRequisitionDocuments,
  showRequisition,
  specimenLabelDocuments,
  isLoadingSpecimenLabelDocuments,
  showSpecimenLabel,
  aoeGroups,
  isLoadingAoeGroups,
  showAoe,
  defaultOpen,
}: LabOrderOrderingProps): React.ReactElement => {
  const { t } = useTranslation('records');

  // Controlled rather than `defaultValue` so a post-mount flip of `defaultOpen` (e.g. a still-
  // loading report resolving to "no results") opens the section — a mount-time default wouldn't.
  // The user can still toggle it freely between those transitions.
  const [openValue, setOpenValue] = useState(defaultOpen ? ORDER_DETAILS_VALUE : '');
  useEffect(() => {
    setOpenValue(defaultOpen ? ORDER_DETAILS_VALUE : '');
  }, [defaultOpen]);

  const body = (
    <>
      {sections.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          {sections.map(({ title, value, preserveWhitespace }) => (
            <Fragment key={title}>
              <dt className="font-medium text-muted-foreground">{title}</dt>
              <dd className={preserveWhitespace ? 'whitespace-pre-wrap' : undefined}>{value}</dd>
            </Fragment>
          ))}
        </dl>
      )}

      {showAoe && <OrderEntryQuestionsSection groups={aoeGroups} isLoading={isLoadingAoeGroups} />}

      {showRequisition && (
        <RequisitionDocumentsSection documents={requisitionDocuments} isLoading={isLoadingRequisitionDocuments} />
      )}

      {showSpecimenLabel && (
        <SpecimenLabelSection documents={specimenLabelDocuments} isLoading={isLoadingSpecimenLabelDocuments} />
      )}
    </>
  );

  return (
    <Accordion type="single" collapsible value={openValue} onValueChange={setOpenValue}>
      <AccordionItem value={ORDER_DETAILS_VALUE}>
        <AccordionTrigger className="text-lg font-semibold">{t('orderDetails')}</AccordionTrigger>
        <AccordionContent className="space-y-6 pt-2">{body}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
